import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

// helpers
function useQuery(search) {
  return useMemo(() => new URLSearchParams(search), [search]);
}

const safe = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (s.toLowerCase() === "null") return "";
  return s;
};

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return safe(v);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const TenderDetailReport = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = useQuery(search);

  // ✅ accept both styles
  const contractId = query.get("contractId") || query.get("contractid");
  const tenderDetailsId =
    query.get("tenderDetailsId") || query.get("tenderdetailsid") || query.get("tenderId") || query.get("tenderid");

  const [loading, setLoading] = useState(false);
  const [main, setMain] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [otherItems, setOtherItems] = useState([]);

  useEffect(() => {
    if (!contractId || !tenderDetailsId) return;

    const fetchReport = async () => {
      setLoading(true);
      try {
        // ✅ This API should return main + taxes + otherItems
        // Example response:
        // { main:[{...}], taxes:[...], otherItems:[...] }
        const res = await axios.get(`${BASE_URL}/api/tender/detailsReport`, {
          params: { contractId, tenderId: tenderDetailsId },
        });

        setMain(res.data?.main?.[0] || null);
        setTaxes(res.data?.taxes || []);
        setOtherItems(res.data?.otherItems || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [contractId, tenderDetailsId]);

  const handlePrint = () => window.print();

  // Build rows exactly like your static design (but dynamic values)
  const page1Rows = useMemo(() => {
    if (!main) return [];

    return [
      { sno: "1.", label: "Name Of The Work", details: safe(main.fld_contractname) },
      { sno: "2.", label: "Tender Ref No", details: safe(main.fld_tender_refno) },
      { sno: "3.", label: "Client", details: safe(main.fld_clientname) },
      { sno: "4.", label: "Location", details: safe(main.fld_Worklocationname) },
      { sno: "5.", label: "Estimated Value", details: safe(main.fld_contractvalue1 || main.fld_contractvalue) },
      { sno: "6.", label: "Estimated Remarks", details: safe(main.fld_contractvalueremarks) },
      { sno: "7.", label: "EMD Value", details: safe(main.fld_emdbidsecurityamt1 || main.fld_emdbidsecurityamt) },
      { sno: "8.", label: "EMD Remarks", details: safe(main.fld_emdbidsecurityamtremarks) },
      { sno: "9.", label: "Cost Of Tender", details: safe(main.fld_costoftenderdocument1 || main.fld_costoftenderdocument) },
      { sno: "10.", label: "Remarks", details: safe(main.fld_remarks) },
      { sno: "11.", label: "Bid Validity", details: safe(main.fld_bidvaliditydays1 || main.fld_bidvaliditydays) },
      {
        sno: "12.",
        label: "Site Visit And Pre Bid Meeting Date",
        details: fmtDate(main.fld_sitevisitprebidmeetingdate),
      },
      { sno: "13.", label: "Site Visit And Pre Bid Meeting Remarks", details: safe(main.fld_sitevisitprebidmeetingremarks) },
      {
        sno: "14.",
        label: "Tender Submission Date & Venue",
        details: [fmtDate(main.fld_tendersubmissiondatevenue), safe(main.fld_clientname)]
          .filter(Boolean)
          .join("\n"),
      },
      { sno: "15.", label: "Tender System(Two Cover/Single Cover)", details: safe(main.fld_tendersystemtwosingle) },
      { sno: "16.", label: "Completion Period", details: safe(main.fld_contractduration) },
    ];
  }, [main]);

  const preQualRows = useMemo(() => {
    if (!main) return [];
    return [
      { sno: "1.", label: "Financial TurnOver", details: safe(main.fld_financialturnover1 || main.fld_financialturnover) },
      { sno: "2.", label: "Technical Qualification", details: safe(main.fld_technicalqualification1 || main.fld_technicalqualification) },
    ];
  }, [main]);

  const guaranteeRows = useMemo(() => {
    if (!main) return [];
    return [
      { sno: "1.", label: "Performance Guarantee", details: safe(main.fld_performanceguarantee1 || main.fld_performanceguarantee) },
      { sno: "2.", label: "Release Of PBG", details: safe(main.fld_releaseofppg1 || main.fld_releaseofppg) },
      { sno: "3.", label: "Retention Money", details: safe(main.fld_retentionamount1 || main.fld_retentionamount) },
      { sno: "4.", label: "Release Of Retention Money", details: safe(main.fld_releaseofretentionmoney1 || main.fld_releaseofretentionmoney) },
      { sno: "5.", label: "Defect Liability Period", details: safe(main.fld_defectliabilityperiod) },
      { sno: "6.", label: "Mobilization Advance", details: safe(main.fld_mobilisationadvance1 || main.fld_mobilisationadvance) },
    ];
  }, [main]);

  // Taxes: take from API (fallback to fixed labels if API sends only names)
  const taxDefaults = [
    "Works Contract Tax / VAT",
    "Service Tax",
    "TN Workers Welfare Cess",
    "ESI",
    "PF",
  ];

  const taxRows = useMemo(() => {
    if (taxes?.length) {
      return taxes.map((t, idx) => ({
        sno: String(idx + 1),
        label: safe(t.fld_taxtype || t.fld_description || taxDefaults[idx] || "Tax"),
        details: safe(t.fld_description1 || t.fld_value || ""),
      }));
    }
    // if API returns empty, still show 5 rows like old report
    return taxDefaults.map((name, idx) => ({ sno: String(idx + 1), label: name, details: "" }));
  }, [taxes]);

  const otherDefaults = [
    "MaterialSupply",
    "WaterSupply",
    "ElectricitySupply",
    "LandworkCamp",
    "Escalation",
    "PaymentTerms",
    "DateofCommencement",
  ];

  const otherRows = useMemo(() => {
    if (otherItems?.length) {
      return otherItems.map((o, idx) => ({
        sno: String(idx + 1),
        label: safe(o.fld_Otherdetails || o.fld_itemdescription || otherDefaults[idx] || "Other Item"),
        details: safe(o.fld_Otheritem1 || o.fld_Otheritem2 || ""),
      }));
    }
    // keep old static rows if empty
    return otherDefaults.map((name, idx) => ({ sno: String(idx + 1), label: name, details: "" }));
  }, [otherItems]);

  const preparedBy = safe(main?.preparedby) || "Waiting";
  const preparedByDate = main?.preparedbydate ? fmtDate(main.preparedbydate) : "";
  const authorizedBy = safe(main?.Authorizedby) || "Waiting";
  const approvedBy = safe(main?.Approvedby) || "Waiting";

  return (
    <div className="p-6">
      {/* Top Buttons (not printed) */}
      <div className="flex justify-end gap-3 mb-4 print:hidden">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2 rounded-lg bg-green-600 text-white font-semibold"
        >
          Back
        </button>
        <button
          onClick={handlePrint}
          className="px-5 py-2 rounded-lg bg-green-600 text-white font-semibold"
        >
          Print
        </button>
      </div> 

      {/* Error */}
      {!contractId || !tenderDetailsId ? (
        <div className="text-red-600 font-semibold">
          contractId / tenderDetailsId missing in URL
        </div>
      ) : loading ? (
        <div className="text-sm">Loading...</div>
      ) : !main ? (
        <div className="text-red-600 font-semibold">No data found</div>
      ) : (
        <div className="bg-white text-black p-8 font-sans text-sm">
          {/* Header Section */}
          <div className="border-2 border-black mb-6">
            <div className="flex">
              {/* Logo Section */}
              <div className="w-32 h-20 border-r-2 border-black flex items-center justify-center bg-black text-white">
                <div className="text-center">
                  <div className="text-xs font-bold">NISSI INFOTECH PVT LT</div>
                </div>
              </div>

              {/* Company Details */}
              <div className="flex-1 p-3 text-center">
                <h1 className="text-lg font-bold mb-1">Nissi Infotech</h1>
                <div className="text-xs">
                  <div>Nissi</div>
                  <div className="font-bold">CIVIL CONSTRUCTION</div>
                  <div>
                    3/42-12, Nagappa colony, kavundampalayam,erode-638009, Tamil Nadu, India .
                  </div>
                  <div>
                    Phone No. 9003734645 &nbsp;&nbsp; infraconsoft.implementation@nisssinfotech.com
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Report Title */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold">Tender Detail Report</h2>
          </div>

          {/* Main Table */}
          <table className="w-full border-collapse border border-black mb-6">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-3 py-2 text-left w-12">S.No</th>
                <th className="border border-black px-3 py-2 text-left w-48">Description</th>
                <th className="border border-black px-3 py-2 text-left">Details</th>
                <th className="border border-black px-3 py-2 text-left w-20">Page No</th>
              </tr>
            </thead>
            <tbody>
              {page1Rows.map((r, idx) => (
                <tr key={idx}>
                  <td className="border border-black px-3 py-2">{r.sno}</td>
                  <td className="border border-black px-3 py-2">{r.label}</td>
                  <td className="border border-black px-3 py-2 whitespace-pre-line">{r.details}</td>
                  <td className="border border-black px-3 py-2"></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Section II - Pre Qualification Requirement */}
          <table className="w-full border-collapse border border-black mb-6">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-3 py-2 text-left font-bold" colSpan="4">
                  II. Pre Qualification Requirement
                </th>
              </tr>
            </thead>
            <tbody>
              {preQualRows.map((r, idx) => (
                <tr key={idx}>
                  <td className="border border-black px-3 py-2 w-12">{r.sno}</td>
                  <td className="border border-black px-3 py-2 w-48">{r.label}</td>
                  <td className="border border-black px-3 py-2">{r.details}</td>
                  <td className="border border-black px-3 py-2 w-20"></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Section III - Guarantee Details */}
          <table className="w-full border-collapse border border-black mb-6">
            <thead>
              <tr className="bg-gray-200">
                <th className="border border-black px-3 py-2 text-left font-bold" colSpan="4">
                  III. Guarantee Details
                </th>
              </tr>
            </thead>
            <tbody>
              {guaranteeRows.map((r, idx) => (
                <tr key={idx}>
                  <td className="border border-black px-3 py-2 w-12">{r.sno}</td>
                  <td className="border border-black px-3 py-2 w-48">{r.label}</td>
                  <td className="border border-black px-3 py-2">{r.details}</td>
                  <td className="border border-black px-3 py-2 w-20"></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Page Footer */}
          <div className="text-right text-xs mt-4">
            <strong>Page 1 of 2</strong>
          </div>

          {/* Page Break */}
          <div className="page-break-before-always mt-8 pt-8">
            <table className="w-full border-collapse border border-black mb-6">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-black px-3 py-2 text-left w-12">S.No</th>
                  <th className="border border-black px-3 py-2 text-left w-48">Description</th>
                  <th className="border border-black px-3 py-2 text-left">Details</th>
                  <th className="border border-black px-3 py-2 text-left w-20">Page No</th>
                </tr>
              </thead>
              <tbody>
                {/* Section IV - Taxes */}
                <tr className="bg-gray-200">
                  <td className="border border-black px-3 py-2 font-bold" colSpan="4">
                    IV. Taxes
                  </td>
                </tr>

                {taxRows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="border border-black px-3 py-2">{r.sno}</td>
                    <td className="border border-black px-3 py-2">{r.label}</td>
                    <td className="border border-black px-3 py-2">{r.details}</td>
                    <td className="border border-black px-3 py-2"></td>
                  </tr>
                ))}

                {/* Section V - Group Insurance */}
                <tr className="bg-gray-200">
                  <td className="border border-black px-3 py-2 font-bold" colSpan="4">
                    V. Group Insurance
                  </td>
                </tr>
                <tr>
                  <td className="border border-black px-3 py-2">1</td>
                  <td className="border border-black px-3 py-2">Group Insurance</td>
                  <td className="border border-black px-3 py-2">{safe(main?.fld_groupinsurance)}</td>
                  <td className="border border-black px-3 py-2"></td>
                </tr>

                {/* Section VII - Other Items */}
                <tr className="bg-gray-200">
                  <td className="border border-black px-3 py-2 font-bold" colSpan="4">
                    VII. Other Items
                  </td>
                </tr>

                {otherRows.map((r, idx) => (
                  <tr key={idx}>
                    <td className="border border-black px-3 py-2">{r.sno}</td>
                    <td className="border border-black px-3 py-2">{r.label}</td>
                    <td className="border border-black px-3 py-2">{r.details}</td>
                    <td className="border border-black px-3 py-2"></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Signature Section */}
            <div className="grid grid-cols-3 gap-8 mt-12 mb-8">
              <div>
                <div className="font-bold mb-2">Prepared By</div>
                <div>{preparedBy}</div>
                <div className="text-xs">{preparedByDate}</div>
              </div>
              <div>
                <div className="font-bold mb-2">Authorized By</div>
                <div>{authorizedBy}</div>
              </div>
              <div>
                <div className="font-bold mb-2">Approved By</div>
                <div>{approvedBy}</div>
              </div>
            </div>

            {/* Page Footer */}
            <div className="flex justify-between items-center text-xs mt-8">
              <div>Report Created by infraconsoft Version 2.1. www.infraconsoft.com</div>
              <div>
                <strong>Page 2 of 2</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print CSS */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .page-break-before-always { page-break-before: always; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
};

export default TenderDetailReport;
